import { cwfPublicUrl } from "@/lib/cwfPublicUrl";
import manifest from "./sound-effects.json";
import { useAudioSettings } from "@/stores/audioSettings";
import type {
  ChaosSoundClip,
  SoundClip,
  SoundEffectsManifest,
  TriviaSoundEvent,
  UiSoundEvent,
} from "./types";
import { TRIVIA_SOUND_EVENTS, UI_SOUND_EVENTS } from "./types";

export type {
  ChaosSoundClip,
  SoundClip,
  SoundEffectsManifest,
  TriviaSoundEvent,
  UiSoundEvent,
};
export { TRIVIA_SOUND_EVENTS, UI_SOUND_EVENTS };

export const soundEffectsManifest = manifest as SoundEffectsManifest;

/** FRVR / channel fullscreen ads — mute without touching persisted user prefs. */
let platformAudioSuspendDepth = 0;

export function beginPlatformAudioSuspend(): void {
  platformAudioSuspendDepth += 1;
}

export function endPlatformAudioSuspend(): void {
  platformAudioSuspendDepth = Math.max(0, platformAudioSuspendDepth - 1);
}

function platformAudioMultiplier(): number {
  return platformAudioSuspendDepth > 0 ? 0 : 1;
}

function clipBelongsToMusic(clip: SoundClip): boolean {
  return soundEffectsManifest.music.some((m) => m.id === clip.id && m.src === clip.src);
}

/** User preference multiplier 0–1; SSR / no window → 1. */
function userOutputMultiplier(isMusic: boolean): number {
  if (typeof window === "undefined") return 1;
  return useAudioSettings.getState().outputMultiplier(isMusic);
}

const CLIP_CATEGORIES: (keyof SoundEffectsManifest)[] = [
  "music",
  "place",
  "cook",
  "ui",
  "match",
  "trivia",
  "chaos",
];

/** Resolve effective playback volume using manifest defaults when a clip omits `volume`. */
export function clipVolume(clip: SoundClip): number {
  const d = soundEffectsManifest.defaults?.volume;
  if (clip.volume != null) return clip.volume;
  if (d != null) return d;
  return 1;
}

/** Volume for manifest clip × user Audio settings (music vs SFX). */
export function effectivePlaybackVolume(clip: SoundClip): number {
  const base = clipVolume(clip);
  const isMusic = clipBelongsToMusic(clip);
  return base * userOutputMultiplier(isMusic) * platformAudioMultiplier();
}

/** All clips flattened by `id` (last wins if IDs collide — should not happen). */
export function clipsById(): Map<string, SoundClip> {
  const m = new Map<string, SoundClip>();
  for (const key of CLIP_CATEGORIES) {
    const list = soundEffectsManifest[key];
    if (!Array.isArray(list)) continue;
    for (const c of list as SoundClip[]) {
      m.set(c.id, c);
    }
  }
  const d = soundEffectsManifest.chaosDefault;
  if (d) m.set(d.id, d);
  return m;
}

export function getClipById(id: string): SoundClip | undefined {
  return clipsById().get(id);
}

const TRIVIA_BINDING_DEFAULTS: Record<TriviaSoundEvent, string> = {
  spinStart: "trivia_spin",
  spinLand: "trivia_spin_land",
  answerCorrect: "trivia_correct",
  answerWrong: "trivia_wrong",
  crownLand: "trivia_crown",
  powerUpFifty: "trivia_power_up_fifty",
  powerUpTime: "trivia_power_up_time",
  powerUpSkip: "trivia_power_up_skip",
};

/** Resolve clip id for a trivia duel event from `triviaBindings`, with built-in defaults. Empty string in JSON = no sound. */
export function resolveTriviaClipId(event: TriviaSoundEvent): string | undefined {
  const raw = soundEffectsManifest.triviaBindings?.[event];
  if (raw === "") return undefined;
  if (raw != null && String(raw).trim() !== "") return String(raw).trim();
  return TRIVIA_BINDING_DEFAULTS[event];
}

const UI_BINDING_DEFAULTS: Record<UiSoundEvent, string> = {
  buttonClick: "button_click",
};

/** Resolve clip id for a UI event from `uiBindings` (default: `button_click`). */
export function resolveUiClipId(event: UiSoundEvent): string | undefined {
  const raw = soundEffectsManifest.uiBindings?.[event];
  if (raw === "") return undefined;
  if (raw != null && String(raw).trim() !== "") return String(raw).trim();
  return UI_BINDING_DEFAULTS[event];
}

/** One-shot lookup when a chaos card resolves (`cardKey` matches engine card keys). */
export function getChaosClipByCardKey(cardKey: string): ChaosSoundClip | undefined {
  const hit = soundEffectsManifest.chaos.find((c) => c.cardKey === cardKey);
  if (hit) return hit;
  const fb = soundEffectsManifest.chaosDefault;
  if (!fb) return undefined;
  return { ...fb, cardKey };
}

/** `HTMLAudioElement.preload` for this clip (streaming clips default to manifest `defaults.streaming`). */
export function clipPreloadPolicy(
  clip: SoundClip,
): "none" | "metadata" | "auto" {
  if (clip.preload != null) return clip.preload;
  if (clip.streaming) {
    const d = soundEffectsManifest.defaults?.streaming?.preload;
    if (d != null) return d;
    return "none";
  }
  return "auto";
}

/** Wire manifest fields onto an `<audio>` element (same element can be reused when switching routes if `src` matches). */
export function applyClipToHtmlAudio(
  el: HTMLAudioElement,
  clip: SoundClip,
): void {
  el.src = cwfPublicUrl(clip.src);
  el.loop = clip.loop ?? false;
  el.volume = effectivePlaybackVolume(clip);
  el.preload = clipPreloadPolicy(clip);
}
